import { z } from "zod";

/**
 * Companies control-plane shared contract (Strangler-Fig migration, manifest-6,
 * slice a14 / N-3).
 *
 * Single source of truth for the `GET /companies/main` wire shape shared by the
 * web SPA (`src/repositories/companies.ts` -> `useMainCompanyId`) and the NestJS
 * slice (`apps/api/src/companies/*`). The web repo `.parse()`s the Nest response
 * against `mainCompanyResponseSchema` at the flip boundary, so any FE<->BE drift
 * surfaces at runtime (and the repo falls back to the Supabase RPC path).
 *
 * BE adoption note: `apps/api` does NOT import `@dallali/shared` at runtime (a
 * value import crash-loops the deployed API — it is not a runtime dependency).
 * The BE keeps a field-for-field mirror in
 * `apps/api/src/companies/dto/main-company.dto.ts`; THIS schema is the contract
 * both sides mirror.
 */

/**
 * Response for `GET /companies/main`.
 *
 * `mainCompanyId` is the platform-wide main company id (`companies.is_main`), or
 * `null` when no company is designated main — the FE treats null as "no main
 * configured" and gates dependent flows behind it (never crashes). The id is a
 * UUID; `.uuid()` is version-agnostic (accepts v4 and v7 — never pin a version).
 */
export const mainCompanyResponseSchema = z.object({
  mainCompanyId: z.string().uuid().nullable(),
});

export type MainCompanyResponse = z.infer<typeof mainCompanyResponseSchema>;

/**
 * Row of `GET /companies/super-admin/list` — the
 * `rpc_super_admin_companies_with_counts` TABLE shape VERBATIM (snake_case
 * wire), so the FE's `SuperAdminCompanyRow` (sourced from `Database[...]`)
 * stays structurally identical across the transport flip.
 */
export const superAdminCompanyRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  name_en: z.string().nullable(),
  is_active: z.boolean().nullable(),
  created_at: z.string(),
  admin_count: z.number(),
  user_count: z.number(),
  properties_count: z.number(),
  units_count: z.number(),
  contracts_count: z.number(),
  tenants_count: z.number(),
  owners_count: z.number(),
  employees_count: z.number(),
});
export const superAdminCompaniesResponseSchema = z.array(
  superAdminCompanyRowSchema,
);
export type SuperAdminCompanyRowWire = z.infer<typeof superAdminCompanyRowSchema>;

/** Response of `POST /companies/super-admin/main` (set_main_company wrap). */
export const setMainCompanyResponseSchema = z.object({
  mainCompanyId: z.string().uuid(),
});
export type SetMainCompanyResponse = z.infer<typeof setMainCompanyResponseSchema>;

/**
 * Envelope of `PATCH /companies/super-admin/:id/active` — the
 * `rpc_set_company_active` jsonb envelope VERBATIM. Business failures
 * (self_lockout / forbidden / not_found) arrive IN-BAND with HTTP 200; the FE
 * maps `error_code` to bilingual messages (super-admin Companies page).
 */
export const setCompanyActiveResponseSchema = z.object({
  success: z.boolean(),
  error_code: z.string().optional(),
  company_id: z.string().uuid().optional(),
  is_active: z.boolean().optional(),
});
export type SetCompanyActiveResponse = z.infer<typeof setCompanyActiveResponseSchema>;

/**
 * `GET /companies/current` wire shape — the FE's `CompanySettings`
 * (src/hooks/useSettings.ts) in snake_case. Decimal config columns (vat_rate,
 * management_fee_value, maintenance_approval_threshold) are numbers on the
 * wire. `settings` is the raw JSONB object (whole-object semantics).
 */
export const companySettingsSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  name_en: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  cr_number: z.string().nullable(),
  vat_number: z.string().nullable(),
  unified_number: z.string().nullable(),
  logo_url: z.string().nullable(),
  default_currency: z.string().nullable(),
  vat_rate: z.number().nullable(),
  management_fee_type: z.string().nullable(),
  management_fee_value: z.number().nullable(),
  bank_name: z.string().nullable(),
  bank_account_number: z.string().nullable(),
  bank_iban: z.string().nullable(),
  bank_account_holder_name: z.string().nullable(),
  maintenance_approval_threshold: z.number().nullable(),
  owner_portal_welcome_message: z.string().nullable(),
  tenant_portal_welcome_message: z.string().nullable(),
  settings: z.record(z.unknown()).nullable(),
});
export type CompanySettingsWire = z.infer<typeof companySettingsSchema>;

/**
 * Body of `PATCH /companies/current` — partial of the updatable columns
 * (everything except `id`). Ranges mirror the BE DTO (vat_rate 0–100,
 * management_fee_value ≥ 0); `settings` REPLACES the whole JSON object.
 */
export const companySettingsUpdateSchema = companySettingsSchema
  .omit({ id: true })
  .extend({
    vat_rate: z.number().min(0).max(100).nullable(),
    management_fee_value: z.number().min(0).nullable(),
  })
  .partial();
export type CompanySettingsUpdateWire = z.infer<typeof companySettingsUpdateSchema>;
